import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Sessions are httpOnly cookies set by the backend (sf_session) — never readable by page scripts.
// CSRF: the backend also sets a readable sf_csrf cookie; every mutating request echoes it in X-CSRF-Token.
const csrfToken = () => document.cookie.split("; ").find((c) => c.startsWith("sf_csrf="))?.slice(8) || "";
export const csrfHeaders = () => ({ "X-CSRF-Token": csrfToken() });

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((cfg) => {
  if (!["get", "head", "options"].includes((cfg.method || "get").toLowerCase())) cfg.headers["X-CSRF-Token"] = csrfToken();
  return cfg;
});

// A backend hot-reload/restart leaves the ingress answering 502/503/504 (or refusing connections) for ~2 s.
// Idempotent GETs are retried through that window so a valid session is not mistaken for a logged-out one.
// Connectivity state is broadcast as window events: sf:reconnecting (retrying), sf:online (recovered), sf:offline (retries exhausted).
const RETRY_DELAYS_MS = [600, 1200, 2400, 4800];
export const isTransient = (err) => !err?.response || [502, 503, 504].includes(err.response.status);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
let retrying = 0;
const emit = (name, detail) => window.dispatchEvent(new CustomEvent(name, { detail }));
const retryDone = (outcome, detail) => { retrying = Math.max(0, retrying - 1); if (retrying === 0) emit(outcome, detail); };

api.interceptors.response.use(
  (r) => { if (r.config?.__retry) retryDone("sf:online"); return r; },
  async (err) => {
    const cfg = err?.config || {};
    if (err?.response?.status === 401 && !cfg.url?.endsWith("/auth/login")) window.dispatchEvent(new Event("sf:unauthorized"));
    const attempt = cfg.__retry || 0;
    if ((cfg.method || "get").toLowerCase() === "get" && isTransient(err)) {
      if (attempt < RETRY_DELAYS_MS.length) {
        if (attempt === 0 && retrying++ === 0) emit("sf:reconnecting");
        await sleep(RETRY_DELAYS_MS[attempt]);
        return api({ ...cfg, __retry: attempt + 1 });
      }
      retryDone("sf:offline", describeFailure(err));
    }
    return Promise.reject(err);
  }
);

// 401s and transient transport failures are already handled above (logout / reconnecting banner); don't let pages
// that don't catch them surface as "Uncaught runtime error" overlays or noisy unhandled rejections.
window.addEventListener("unhandledrejection", (e) => {
  if (axios.isAxiosError(e.reason) && (e.reason.response?.status === 401 || isTransient(e.reason))) e.preventDefault();
});

// Human-readable transport diagnosis for self-check / error surfaces.
export function describeFailure(err) {
  const host = (() => { try { return new URL(API).host; } catch { return API; } })();
  if (err?.response) return `HTTP ${err.response.status} from ${host}${[502, 503, 504].includes(err.response.status) ? " (backend restarting behind the proxy)" : ""}`;
  if (err?.code === "ECONNABORTED") return `timeout reaching ${host}`;
  return `network error reaching ${host}${err?.message ? ` — ${err.message}` : ""}`;
}

// Authenticated binary download (PDF/DOCX exports). Throws on non-2xx.
export async function downloadFile(path, filename) {
  const resp = await fetch(`${API}${path}`, { credentials: "include" });
  if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
  const url = URL.createObjectURL(await resp.blob());
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// FastAPI 422 validation errors put an array of objects in `detail`; rendering that in a toast/JSX crashes React.
export function apiError(err, fallback = "Something went wrong. Please try again.") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || fallback;
  if (d && typeof d.msg === "string") return d.msg;
  return fallback;
}

export default api;
